import { AppSettings } from "@/AppSettings";
import { SourceDataEventType } from "@/api/dtos";
import IsLoading from "@/components/layouts/IsLoading";
import Show from "@/components/layouts/Show";
import SourceDataItemAuditList from "@/components/lists/audit/SourceDataItemAuditList";
import SourceDataVerificationAuditList from "@/components/lists/audit/SourceDataVerificationAuditList";
import NotesAndQueriesList from "@/components/lists/notes/NotesAndQueriesList";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import ParticipantNotesAndQueriesContext from "@/contexts/data/ParticipantNotesAndQueriesContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useParticipantSourceDataItemAudit from "@/hooks/data/useParticipantSourceDataItemAudit";
import useParticipantSourceDataVerificationAudit from "@/hooks/data/useParticipantSourceDataVerificationAudit";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, styled } from "@mui/material";
import { FC, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

const TitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginBottom: theme.spacing(2), 
    fontSize: '1.5rem',
    gap: theme.spacing(2),
    alignItems: 'center'
}))

const ChipBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginLeft: 'auto',
    gap: theme.spacing(2),

    '& > div': {
        
        paddingLeft: theme.spacing(0.5),
    },
    '& svg': {
        fontSize: '1.5rem'
    }
}))

const ParticipantSourceDataItemValueVerificationAudit: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const { studyEventOid, studyEventRepeat, caseReportFormOid, itemGroupName, itemGroupRepeat, itemName } = useParams();

    const { data: audits, loading: auditsLoading, validating: auditsValidating } = useParticipantSourceDataVerificationAudit(
        trial?.id,
        participant?.id,
        studyEventOid, 
        studyEventRepeat ? +studyEventRepeat : undefined, 
        caseReportFormOid, 
        itemGroupName, 
        itemGroupRepeat ? +itemGroupRepeat : undefined, 
        itemName
    )



    const event = useEvent(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!
    )

    const form = useForm(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid
    )

    if (!trial) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Trial</AlertTitle>
                No trial was found.
            </Alert>
        )
    }

    if (!participant) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Participant</AlertTitle>
                No participant was found.
            </Alert>
        )
    }

    if (!metadata) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Metadata Could Not Be Retrieved</AlertTitle>
                The metadata for the {trial.name} trial could not be retrieved from OpenClinica.
            </Alert>
        )
    }

    if (!trialConfiguration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Configuration Could Not Be Retrieved</AlertTitle>
                The global configuration for the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!configuration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Allocated Configuration Could Not Be Retrieved</AlertTitle>
                The allocated configuration for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!sourceData) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Source Data Could Not Be Retrieved</AlertTitle>
                The source data for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!form?.formVersion?.caseReportFormVersionOid) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Case Report Form Version Could Not Be Retrieved</AlertTitle>
                The case report form version could not be retrieved.
            </Alert>
        )
    }
    
    return (
        <>
            {/* <IsLoading
                name="source-data-item-audits"
                loading={auditsLoading}
                validating={auditsValidating}
            /> */}
            <Show show={!auditsLoading}>
                <SourceDataVerificationAuditList 
                    trialId={trial?.id} 
                    masterGroupId={masterGroup?.id}
                    coordinatingGroupId={coordinatingGroup?.id}
                    institutionId={institution?.id}
                    participantId={participant?.id}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={+studyEventRepeat!}
                    caseReportFormOid={caseReportFormOid}
                    caseReportFormVersionOid={form.formVersion.caseReportFormVersionOid}
                    itemGroupName={itemGroupName}
                    itemGroupRepeat={itemGroupRepeat ? +itemGroupRepeat! : 1}
                    itemName={itemName}
                    
                    audits={audits}
                />
            </Show>
        </>
    )
};

export default ParticipantSourceDataItemValueVerificationAudit;